import { useState, type ReactElement } from 'react';
import { EntitySort, type EntityListDto } from '@gr/core/content/entities/dto';
import { BlogPermission } from '@gr/core/accounts/workspaces/permissions';
import { useConfig, useNavigate, useServices, useT } from '../../../app/di';
import { usePagedResource, useResource, type Paging } from '../../../shared/hooks';
import { Alert } from '../../../ui-kit/Alert/Alert';
import { Button, ShowMoreButton } from '../../../ui-kit/Button/Button';
import { EmptyState } from '../../../ui-kit/EmptyState/EmptyState';
import { BareInput, BareSelect } from '../../../ui-kit/Field/Field';
import { Loading } from '../../../ui-kit/Loading/Loading';
import { PageHeader } from '../../../ui-kit/PageHeader/PageHeader';
import { useBlog } from '../../accounts/workspaces';
import { EntityTable } from './EntityTable/EntityTable';

/** Страница записей: сами записи и заголовки записей, на которые они ссылаются. */
const ENTITY_PAGING: Paging<EntityListDto> = {
  count: (page) => page.items.length,
  take: (page, size) => ({ ...page, items: page.items.slice(0, size) }),
  append: (loaded, next) => ({ items: [...loaded.items, ...next.items], referenceTitles: { ...loaded.referenceTitles, ...next.referenceTitles } }),
};

// ENT-UC-6
export function EntityListPage({ typeId }: { typeId: string }): ReactElement {
  const { api } = useServices();
  const { t, te } = useT();
  const { blog, can } = useBlog();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<EntitySort>(EntitySort.Title);
  const type = useResource(() => api.entities.type(blog.id, typeId), [api, blog.id, typeId]);
  const { ui } = useConfig();
  const list = usePagedResource((page) => api.entities.list(blog.id, typeId, { q, sort, ...page }), [api, blog.id, typeId, q, sort], ENTITY_PAGING, ui.lists.pageSize);

  if (type.error) return <Alert tone="danger">{te(type.error)}</Alert>;
  return (
    <div className="u-stack-lg">
      <PageHeader
        title={type.data?.pluralName ?? '…'}
        actions={
          can(BlogPermission.EditContent) ? (
            <Button variant="primary" icon="plus" onClick={() => navigate('entity', { blogId: blog.id, typeId, entityId: 'new' })}>
              {t('entities.list.create')}
            </Button>
          ) : null
        }
      />
      <div className="u-row">
        <div className="u-grow u-measure-sm">
          <BareInput label={t('entities.list.search')} placeholder={t('entities.list.search')} value={q} onChange={(e) => setQ(e.target.value)} type="search" />
        </div>
        <BareSelect
          label={t('entities.list.sort')}
          value={sort}
          onChange={(e) => setSort(e.target.value as EntitySort)}
          options={[
            { value: EntitySort.Title, label: t('entities.list.sortTitle') },
            { value: EntitySort.Updated, label: t('entities.list.sortUpdated') },
          ]}
        />
      </div>
      {(list.loading && !list.data) || !type.data ? (
        <Loading label={t('common.loading')} />
      ) : list.error ? (
        <Alert tone="danger" title={t('common.loadFailed')}>
          <Button size="sm" onClick={() => void list.reload()}>
            {t('common.retry')}
          </Button>
        </Alert>
      ) : (
        <EntityTable
          blogId={blog.id}
          type={type.data}
          entities={list.data?.items ?? []}
          titles={list.data?.referenceTitles ?? {}}
          maxFieldColumns={ui.entities.table.maxFieldColumns}
          empty={<EmptyState title={q ? t('entities.list.emptySearch') : t('entities.list.empty')} />}
        />
      )}
      <ShowMoreButton visible={list.hasMore && !list.error} loading={list.loadingMore} onClick={() => void list.loadMore()}>
        {t('common.showMore')}
      </ShowMoreButton>
    </div>
  );
}
